import '@forest/native-diagnostics'

import {
  getProducts,
  getReceiptSystemAccountIdSafe,
  initializeNativeBridge,
  native,
} from '@forest/native-services'
import {
  type Failure,
  failureCode,
  failureOf,
  tagFailure,
  withFailure,
} from '@forest/paywall-core'
import { showDialog } from '~/components/dialog'
import { getRemoteStates } from '~/lib/get-remote-states'
import { productsReady } from '~/lib/message'
import { appVersion, language, platform } from '~/lib/params'
import { selectPlanVariant } from '~/lib/select-plan-variant'
import { m } from '~/paraglide/messages'
import { overwriteGetLocale, type Locale } from '~/paraglide/runtime'
import { api } from '~/services/api'
import { $receiptSystemAccountId, userInfoSource } from '~/store'

const showDataLoadFailDialog = (failure: Failure) =>
  showDialog({
    title: m.dialog_data_load_fail_title(),
    description: withFailure(m.dialog_data_load_fail_description(), failure),
    buttonText: m.OK(),
    buttonAction: () => native.general.close(),
  })

overwriteGetLocale(() => language as Locale)

initializeNativeBridge({
  bifrost: { enableDebug: import.meta.env.MODE !== 'production' },
})

const userInfoPromise = userInfoSource.getReady()

// Kick off getProducts as early as possible; it starts native prefetches immediately.
const productsPromise = getProducts({
  cacheKeyPrefix: 'passEventPaywall.products.v1',
  api,
  appVersion,
  platform,
  deferredUserInfo: userInfoPromise,
  env: import.meta.env.DEV
    ? 'dev'
    : (import.meta.env.MODE as 'production' | 'staging'),
  shouldMock: import.meta.env.DEV,
})

void getReceiptSystemAccountIdSafe(appVersion).then((id) => {
  $receiptSystemAccountId.set(id)
})

void (async () => {
  try {
    // Each load tags its own failure, so the dialog can name the step.
    const userInfo = await userInfoPromise.catch((error: unknown) => {
      throw tagFailure(error, 'load_user_info')
    })

    const [products, { chinaAndroidTrialEligibility }] = await Promise.all([
      productsPromise.catch((error: unknown) => {
        throw tagFailure(error, 'load_products')
      }),
      getRemoteStates(userInfo.user_id).catch((error: unknown) => {
        throw tagFailure(error, 'load_trial_eligibility')
      }),
    ])

    const productIds = [
      products.annualOriginal.product_id,
      products.annualEarlyBird.product_id,
      products.monthlyOriginal.product_id,
      products.monthlyEarlyBird.product_id,
      products.monthlyNoTrial?.product_id,
    ]
      .filter((id): id is number => id !== undefined)
      .sort((a, b) => a - b)
      .join(',')
    native.general.logEvent({
      event_name: 'pass_event_paywall_products_config',
      event_params: { product_ids: productIds },
    })

    const variant = selectPlanVariant({
      platform,
      products,
      chinaAndroidTrialEligibility,
    })
    if (variant.kind === 'error') {
      // A trial-eligible annual plan needs the no-trial monthly one beside it.
      showDataLoadFailDialog({
        code: failureCode('load_products', 'plan_missing'),
      })
      native.general.pageReady()
      return
    }

    productsReady.done(products)

    native.general.logEvent({
      event_name: 'page_view',
      event_params: { page_name: 'game_pass_countdown_paywall' },
    })
    native.general.pageReady()
  } catch (error) {
    console.error('Failed to get user info or remote states', error)
    showDataLoadFailDialog(failureOf(error, 'load'))
    native.general.pageReady()
  }
})()
